import { LibraryStore } from '@stores/Library/Library';
import { libraryFavorites } from '@stores/LibraryFavorites';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useEffect, useMemo } from 'react';

/**
 * Keeps the `libraryFavorites` store in step with the libraries the manager knows about:
 * hydrates it, then prunes pins whose library no longer exists.
 *
 * Split out from `useFavoriteLibraries` so a surface that only needs the *ordering* (the
 * Libraries page, where the pin glyphs subscribe to the store themselves) can drive the
 * store without reading pin state.
 *
 * Takes no arguments on purpose. It used to derive the id set from a passed
 * `LibraryStore[]`, and every caller had `libraryManager.libraries` to hand — which is the
 * wrong list for this, since it hides a library whose `data` a failed refresh nulled, and
 * pruning *persists*. Reading `knownLibraryIds` here removes the chance of passing the
 * plausible-but-wrong one.
 *
 * **Must be called from an `observer` component** (both current callers are), so the effects
 * below re-run when the observables in their dependency arrays change.
 */
export function useLibraryFavoritesSync() {
  const libraryManager = useLibraryManager();

  // Hydration doesn't wait on the library list — only pruning needs it. Split apart, the
  // settings read starts in parallel with `/library` instead of after it.
  //
  // Keyed on the hydration state rather than `[]` so a `reset()` (logout, or switching account)
  // is re-hydrated by whatever surface is mounted. With `[]` deps, a reset while the Libraries
  // page stayed mounted left `hydrated` false forever — and its first-paint gate reads that, so
  // the list would never come back. `ensureHydrated` returns early once true, so the false→true
  // edge this causes is a no-op rather than a loop.
  //
  // `hydrationEpoch` as well as `hydrated`, because a reset landing *during* an in-flight read
  // leaves `hydrated` already false: MobX doesn't notify on an unchanged value, so that alone
  // would never re-trigger this.
  useEffect(() => {
    void libraryFavorites.ensureHydrated();
  }, [libraryFavorites.hydrated, libraryFavorites.hydrationEpoch]);

  // Drop pins for libraries that no longer exist. No run-once flag: re-running is what
  // cleans up deletions that happen mid-session, and the store's equality bail-out makes
  // the repeat passes free.
  //
  // `hydrated` is a dependency because `pruneMissing` no-ops before hydration: the settings read
  // and `/library` race, so if the read lands after the last change to `knownLibraryIds` this
  // would otherwise never prune again for the whole session.
  const knownLibraryIds = libraryManager.knownLibraryIds;
  useEffect(() => {
    void libraryFavorites.pruneMissing(knownLibraryIds);
  }, [knownLibraryIds, libraryFavorites.hydrated]);
}

/**
 * `useLibraryFavoritesSync` plus the pinned libraries resolved to `LibraryStore`s, in pin
 * order (most recently pinned first) — which is what the Workspace signposts want, and the
 * one place pin *recency* matters rather than pin membership.
 *
 * Takes no arguments for the same reason as the sync hook: the list it resolves against has to be
 * the manager's, and asking callers to pass it in only creates the chance of passing another.
 *
 * **Must be called from an `observer` component** — the returned values are derived from
 * observables, so a non-observer caller would render once with an empty pin list and
 * never update.
 *
 * It deliberately holds no state of its own. The store is the sole source of truth;
 * mirroring the ids back into `useState` (as this hook used to) is what would let two
 * instances of it disagree, and is the one way to create a render loop here.
 */
export function useFavoriteLibraries(): LibraryStore[] {
  const libraryManager = useLibraryManager();
  useLibraryFavoritesSync();

  const libraries = libraryManager.libraries;
  const favoriteIds = libraryFavorites.favoriteIds;

  // Resolve favorite IDs to LibraryStore objects, preserving order. A Map avoids a
  // linear scan per favorite.
  const favoriteLibraries = useMemo(() => {
    const byId = new Map(libraries.map((l) => [l.libraryId, l]));
    return favoriteIds.reduce<LibraryStore[]>((acc, id) => {
      const lib = byId.get(id);
      if (lib) acc.push(lib);
      return acc;
    }, []);
  }, [favoriteIds, libraries]);

  // Returns only this. Whatever renders a pin subscribes to `libraryFavorites` directly
  // (`LibraryGridCard`, `PinCell`), which is what keeps pin state out of the Libraries table's
  // `columns` dependencies — handing a set or a toggle back through a hook invites a caller to
  // thread it as a prop again.
  return favoriteLibraries;
}
